import { Dispatch, FC, SetStateAction, useEffect, useState } from 'react';
import i18next from 'i18next';

import { SurveySubjectTypes } from '~src/interfaces/surveys.interfaces';
import SortSelector from '~src/shared/sortSelector/SortSelector';
import { PeriodValues } from '~src/modules/reports/shared/constants/filterAndSortingKeys';
import { periodOptions } from '~src/modules/reports/shared/constants/periodOptions';
import { FilterTypes } from '~src/modules/surveys/constants/filterOptions';
import { OptionItem } from '~src/shared/selectors/DdSearchContent';
import { HeatmapListFilters } from '~src/modules/heatmap/heatmap.types';
import LightTwoColsHeader from '~src/shared/headers/lightTwoColsHeader/LightTwoColsHeader';

import {
  AttrSelectorCol,
  FiltersRow,
  FilterTitle,
  SelectorBox,
  FilterCol,
  ComparePeriodSelectorContainer,
} from './header.sc';
import AttributeSelector from './AttributeSelector';
import SurveysSelector from './SurveysSelector';

interface OwnProps {
  t: i18next.TFunction;
  initFiltersObj?: HeatmapListFilters;
  setAttributeFilter: (attributeId: string) => Promise<void>;
  changeSelectedSurvey: (
    surveyGroup: string | null,
    surveyPulse: string | null,
    surveyPeople: string | null,
    activeSelector: SurveySubjectTypes,
  ) => void;
  setActiveSelector: Dispatch<SetStateAction<SurveySubjectTypes>>;
  activeSelector: SurveySubjectTypes;

  selectPeriod(period: string): void;
}

const Header: FC<OwnProps> = (props) => {
  const pulseDefOpt: OptionItem = {
    name: 'All Pulse surveys',
    nameKey: 'x.compare.header.filters.select_pulse_survey.default_option',
    id: 'all_pulse',
  };
  const groupDefOpt: OptionItem = {
    name: 'All Group surveys',
    nameKey: 'x.compare.header.filters.select_group_survey.default_option',
    id: 'all_groups',
  };
  const peopleDefOpt: OptionItem = {
    name: 'All People surveys',
    nameKey: 'x.compare.header.filters.select_people_survey.default_option',
    id: 'all_people',
  };
  const [pulseInitOpt, setPulseInitOpt] = useState<string | null>(null);
  const [groupInitOpt, setGroupInitOpt] = useState<string | null>(null);
  const [peopleInitOpt, setPeopleInitOpt] = useState<string | null>(null);

  useEffect(() => {
    const { initFiltersObj } = props;
    if (initFiltersObj) {
      if (initFiltersObj.surveyPeople) {
        setPeopleInitOpt(initFiltersObj.surveyPeople || null);
        setGroupInitOpt(null);
        setPulseInitOpt(null);
        props.setActiveSelector(SurveySubjectTypes.People);
      } else if (initFiltersObj.surveyGroup) {
        setGroupInitOpt(initFiltersObj.surveyGroup || null);
        setPeopleInitOpt(null);
        setPulseInitOpt(null);
        props.setActiveSelector(SurveySubjectTypes.Groups);
      } else {
        setPulseInitOpt(initFiltersObj.surveyPulse || null);
      }
    }
  }, [props.initFiltersObj]);

  const onAttrSelect = (opt: OptionItem) => {
    props.setAttributeFilter(opt.id);
  };

  const onPulseSelect = (opt: OptionItem) => {
    if (opt.id === 'all_pulse') {
      props.changeSelectedSurvey(null, null, null, SurveySubjectTypes.General);
      setPulseInitOpt(null);
    } else {
      props.changeSelectedSurvey(null, opt.id, null, SurveySubjectTypes.General);
      setPulseInitOpt(opt.id);
    }
    setGroupInitOpt(null);
    setPeopleInitOpt(null);
  };

  const onGroupSelect = (opt: OptionItem) => {
    if (opt.id === 'all_groups') {
      props.changeSelectedSurvey(null, null, null, SurveySubjectTypes.Groups);
      setGroupInitOpt(null);
    } else {
      props.changeSelectedSurvey(opt.id, null, null, SurveySubjectTypes.Groups);
      setGroupInitOpt(opt.id);
    }
    setPulseInitOpt(null);
    setPeopleInitOpt(null);
  };

  const onPeopleSelect = (opt: OptionItem) => {
    if (opt.id === 'all_people') {
      props.changeSelectedSurvey(null, null, null, SurveySubjectTypes.People);
      setPeopleInitOpt(null);
    } else {
      props.changeSelectedSurvey(null, null, opt.id, SurveySubjectTypes.People);
      setPeopleInitOpt(opt.id);
    }
    setPulseInitOpt(null);
    setGroupInitOpt(null);
  };

  return (
    <>
      <LightTwoColsHeader
        title={{ key: 'x.compare.header.title' }}
        descr={{ key: 'x.compare.header.description' }}
      />
      <FiltersRow>
        <AttrSelectorCol>
          <FilterTitle data-translation="x.compare.header.filters.select_attribute.title">
            {props.t('x.compare.header.filters.select_attribute.title')}
          </FilterTitle>
          <SelectorBox>
            <AttributeSelector
              onSelect={onAttrSelect}
              initialOptId={props.initFiltersObj && props.initFiltersObj.attribute}
            />
          </SelectorBox>
        </AttrSelectorCol>
        <div style={{ display: 'flex', overflow: 'auto' }}>
          <FilterCol>
            <FilterTitle data-translation="x.compare.header.filters.select_survey.title">
              {props.t('x.compare.header.filters.select_survey.title')}
            </FilterTitle>
            <SelectorBox $inactive={props.activeSelector !== SurveySubjectTypes.General}>
              <SurveysSelector
                triggerIconName="iconPulse"
                surveyType={SurveySubjectTypes.General}
                filterSubject={FilterTypes.General}
                initialOpt={pulseInitOpt}
                inactive={props.activeSelector !== SurveySubjectTypes.General}
                defaultOpt={pulseDefOpt}
                onSelect={onPulseSelect}
              />
            </SelectorBox>
          </FilterCol>
          <FilterCol>
            <SelectorBox $inactive={props.activeSelector !== SurveySubjectTypes.Groups}>
              <SurveysSelector
                triggerIconName="iconGroups"
                surveyType={SurveySubjectTypes.Groups}
                filterSubject={FilterTypes.Groups}
                initialOpt={groupInitOpt}
                inactive={props.activeSelector !== SurveySubjectTypes.Groups}
                defaultOpt={groupDefOpt}
                onSelect={onGroupSelect}
              />
            </SelectorBox>
          </FilterCol>
          <FilterCol>
            <SelectorBox $inactive={props.activeSelector !== SurveySubjectTypes.People}>
              <SurveysSelector
                triggerIconName="iconPeople"
                surveyType={SurveySubjectTypes.People}
                filterSubject={FilterTypes.People}
                initialOpt={peopleInitOpt}
                inactive={props.activeSelector !== SurveySubjectTypes.People}
                defaultOpt={peopleDefOpt}
                onSelect={onPeopleSelect}
              />
            </SelectorBox>
          </FilterCol>
          <FilterCol>
            <ComparePeriodSelectorContainer $smallHeader={false}>
              <SortSelector<string>
                selectedSorting={
                  props.initFiltersObj && props.initFiltersObj.filterAnsweredRange
                    ? props.initFiltersObj.filterAnsweredRange
                    : PeriodValues.AllTime
                }
                sortList={props.selectPeriod}
                sortingOptions={periodOptions}
                selectorLabelKey=" "
              />
            </ComparePeriodSelectorContainer>
          </FilterCol>
        </div>
      </FiltersRow>
    </>
  );
};

export default Header;
